import * as React from 'react';
import {
    List,
    Datagrid,
    TextField,
    EmailField,
    FunctionField,
    CreateButton,
    SearchInput,
    useNotify,
    Confirm,
    EditButton,
    useRefresh
} from 'react-admin';
import { Box, Typography } from '@mui/material';
import { IconButton } from '../icon-button/IconButton.tsx';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import { MemberService } from '../../services/member/memberService.ts';
import { EmptyBox } from '../empty-box/EmptyBox.tsx';
import { WhatsAppButton } from '../customer/WhatsAppButton.tsx';
import * as Sentry from '@sentry/react';

//TODO: move styles to a separate file if possible

// The registration date in the admin's own locale, like the dates on the
// orders page. The server sends a UTC time without a zone marker, so the "Z"
// is added before parsing, exactly as the orders page does.
const formatRegistered = (value?: string | null) => {
    if (!value) return '—';
    const d = new Date(/[Zz]|[+-]\d\d:\d\d$/.test(value) ? value : value + 'Z');
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString();
};

const MembersTableActions = () => {
    const { t } = useTranslation();
    return (
        <Box
            sx={{
                paddingTop: 'var(--space-72)',
                paddingBottom: 'var(--space-32)',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}
        >
            <Typography variant="h5" sx={{
                paddingLeft: 'var(--space-24)',
                fontWeight: 700,
                fontSize: 'var(--text-xxl)',
            }}>
                {t('resources.members')}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CreateButton icon={<AddIcon />} label={t('members.addMember').toLocaleUpperCase()} sx={{
                    color: 'var(--color-primary)',
                    fontSize: '16px',
                    fontWeight: 700,
                }} />
            </Box>
        </Box>
    )
};

export const MembersTable = () => {
    const { t } = useTranslation();
    const notify = useNotify();
    const memberService = new MemberService();

    // One box for everything the admin knows about a person: a part of the
    // name, the e-mail, or some digits of the phone number. The server tries
    // all three; react-admin sends it as ?q=.
    const memberFilters = [
        <SearchInput
            key="q"
            source="q"
            alwaysOn
            placeholder={t('members.search', 'Name, e-mail or phone')}
            sx={{ minWidth: 320 }}
        />,
    ];

    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [toDelete, setToDelete] = React.useState<number | null>(null);
    const refresh = useRefresh();
    return (
        <>
            <List
                actions={<MembersTableActions />}
                filters={memberFilters}
                perPage={25}
                // Newest members first. A new storeKey so a sort saved in the browser
                // before this change does not override it.
                sort={{ field: 'createdAt', order: 'DESC' }}
                storeKey="members.newestFirst"
                empty={<EmptyBox
                    title={t('members.emptyBox.title')}
                    description={t('members.emptyBox.description')}
                    resource="members"
                />}
                sx={{
                    '& .RaList-main': { px: 4 },
                    '& .RaList-content': {
                        border: '1px solid var(--color-secondary)',
                        borderRadius: 'var(--radius-6)',
                    },
                    '& .RaDatagrid-tbody tr:last-of-type td': {
                        borderBottom: 'none',
                    },
                    '& th.MuiTableCell-root': {
                        paddingLeft: 'var(--space-24)',
                        py: 'var(--space-16)',
                        fontSize: 'var(--text-xs)',
                    },
                    '& td.MuiTableCell-root': {
                        py: 'var(--space-24)',
                        paddingLeft: 'var(--space-24)',
                        fontSize: 'var(--text-xs)',
                    },
                    '& .MuiTablePagination-root': {
                        '& .MuiSelect-select': {
                            paddingRight: '32px !important',
                            paddingLeft: '12px !important',
                        },
                        '& .MuiSelect-icon': {
                            right: '8px',
                        }
                    }
                }}
            >
                <Datagrid
                    bulkActionButtons={false}
                    rowClick={false}
                    sx={{
                        '& .RaDatagrid-headerCell': {
                            fontWeight: 700,
                            letterSpacing: 0.2,
                        },
                    }}
                >
                    <TextField source="id" label={t('members.columns.id')} sortable={false} />
                    {/* Clicking a header sorts; the server orders the whole filtered list by
                        that column (role, name, surname, e-mail, registered, orders, credit),
                        blanks last either way, and a second click flips the direction. */}
                    <TextField source="roles" label={t('members.columns.role')} sortBy="roles" />
                    <FunctionField
                        source="name"
                        sortBy="name"
                        label={t('members.columns.name')}
                        render={(record: any) => (record?.name ?? '').trim().split(' ')[0]}
                    />
                    <FunctionField
                        source="surname"
                        sortBy="surname"
                        label={t('members.columns.surname')}
                        render={(record: any) => (record?.name ?? '').trim().split(' ')[1]}
                    />
                    <EmailField source="email" label={t('members.columns.email')} sortBy="email" />
                    <FunctionField
                        source="phoneNumber"
                        label={t('members.columns.phone')}
                        sortable={false}
                        render={(record: any) => record?.phoneNumber ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span style={{ direction: 'ltr', whiteSpace: 'nowrap' }}>{record.phoneNumber}</span>
                                <WhatsAppButton phone={record.phoneNumber} name={[record.name, record.surname].filter(Boolean).join(' ')} size={24} />
                            </Box>
                        ) : null}
                    />
                    <FunctionField
                        source="createdAt"
                        sortBy="createdAt"
                        label={t('members.columns.registered')}
                        render={(record: any) => formatRegistered(record?.createdAt)}
                    />
                    {/* Where the customer came from: their partner ("HILTON (partner)") or
                        the first link / advert ("google / cpc"); blank when unknown. */}
                    <FunctionField
                        source="acquisition"
                        sortBy="acquisition"
                        label={t('members.columns.cameFrom')}
                        render={(record: any) => record?.acquisition || '—'}
                    />
                    {/* Blank rather than 0 / €0.00 for couriers and admins: they
                        place no orders and cannot hold credit, and a zero would
                        imply they could. */}
                    <FunctionField
                        source="orderCount"
                        sortBy="orderCount"
                        label={t('members.columns.orders')}
                        render={(record: any) => (record?.orderCount == null ? '—' : record.orderCount)}
                    />
                    <FunctionField
                        source="creditBalance"
                        sortBy="creditBalance"
                        label={t('members.columns.credit')}
                        render={(record: any) => (
                            record?.creditBalance == null
                                ? '—'
                                : `€${Number(record.creditBalance).toFixed(2)}`
                        )}
                    />
                    <FunctionField
                        label=""
                        sortable={false}
                        render={record => (
                            <>
                                <EditButton icon={<EditIcon />} label="" sx={{
                                    color: 'var(--color-primary)',
                                    minWidth: 'unset !important',
                                    padding: 0,
                                }} />
                                <IconButton icon={<DeleteOutlineIcon />} color="var(--color-text-primary)" onClick={() => {
                                    setConfirmOpen(true);
                                    setToDelete(record.id);
                                }}
                                />
                            </>
                        )}
                    />
                    {/* <FunctionField
                label=""
                sortable={false}
                render={record => (
            )}
            /> */}
                </Datagrid>
            </List>

            <Confirm
                isOpen={confirmOpen}
                title={t('members.dialog.confirmDeletionTitle',)}
                content={t('members.dialog.confirmDeletionBody')}
                confirm={t('common.delete')}
                cancel={t('common.cancel')}
                onConfirm={async () => {
                    try {
                        await memberService.deleteMember(toDelete);
                        notify(t('common.saved'), { type: 'success' });
                        setConfirmOpen(false);
                        refresh();
                        setToDelete(null);
                    } catch (error) {
                        notify(error.response.data.error, { type: 'error' });
                        console.error(error.response.data.error);
                        Sentry.captureException(error);
                        setConfirmOpen(false);
                        setToDelete(null);
                        return;
                    }
                }}
                onClose={() => {
                    setConfirmOpen(false);
                    setToDelete(null);
                }}
            />
        </>
    )
};